import { useEffect, useMemo, useState } from "react";
import Papa from "papaparse";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ClockCounterClockwise, ArrowClockwise, Plus, PencilSimple, Trash,
  Check, X, Scissors, Truck, ArrowsClockwise, UploadSimple, Stack, DownloadSimple, Warning,
} from "@phosphor-icons/react";

const ACTION_META = {
  create: { label: "Create", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: Plus },
  update: { label: "Update", cls: "bg-blue-50 text-blue-700 border-blue-200", Icon: PencilSimple },
  delete: { label: "Delete", cls: "bg-red-50 text-red-700 border-red-200", Icon: Trash },
  approve: { label: "Approve", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: Check },
  reject: { label: "Reject", cls: "bg-red-50 text-red-700 border-red-200", Icon: X },
  cut: { label: "Cut", cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Scissors },
  receive: { label: "Receive", cls: "bg-teal-50 text-teal-700 border-teal-200", Icon: Truck },
  adjust: { label: "Adjust", cls: "bg-purple-50 text-purple-700 border-purple-200", Icon: ArrowsClockwise },
  import: { label: "Import", cls: "bg-slate-100 text-slate-700 border-slate-200", Icon: UploadSimple },
  import_error: { label: "Import error", cls: "bg-red-50 text-red-700 border-red-200", Icon: Warning },
  bulk_update: { label: "Bulk", cls: "bg-slate-100 text-slate-700 border-slate-200", Icon: Stack },
};

const ROLE_CLS = {
  admin: "bg-emerald-50 text-emerald-700 border-emerald-200",
  staff: "bg-slate-100 text-slate-600 border-slate-200",
  viewer: "bg-purple-50 text-purple-700 border-purple-200",
};

function timeAgo(iso) {
  const then = new Date(iso).getTime();
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("all");
  const [entityFilter, setEntityFilter] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (actionFilter !== "all") params.action = actionFilter;
      if (entityFilter !== "all") params.entity_type = entityFilter;
      const { data } = await api.get("/activity-logs", { params });
      setLogs(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [actionFilter, entityFilter]);

  useEffect(() => { api.post("/import-errors/mark-seen").catch(() => {}); }, []);

  const filtered = useMemo(() => {
    return logs.filter((l) => {
      const d = new Date(l.created_at);
      if (fromDate && d < new Date(fromDate + "T00:00:00")) return false;
      if (toDate && d > new Date(toDate + "T23:59:59")) return false;
      return true;
    });
  }, [logs, fromDate, toDate]);

  const grouped = useMemo(() => {
    const g = {};
    for (const l of filtered) {
      const day = new Date(l.created_at).toLocaleDateString(undefined, { weekday: "short", year: "numeric", month: "short", day: "numeric" });
      (g[day] = g[day] || []).push(l);
    }
    return g;
  }, [filtered]);

  const exportCsv = () => {
    if (filtered.length === 0) { toast.error("Nothing to export"); return; }
    const rows = filtered.map((l) => ({
      timestamp: new Date(l.created_at).toISOString(),
      action: l.action,
      entity_type: l.entity_type,
      description: l.description,
      actor: l.actor_name || l.actor_email,
      actor_role: l.actor_role,
    }));
    const csv = Papa.unparse(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "vwa-activity-log.csv"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Activity log exported");
  };

  const clearDates = () => { setFromDate(""); setToDate(""); };

  return (
    <div className="p-8 max-w-[1100px]" data-testid="activity-log-page">
      <header className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Workspace · Audit</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <ClockCounterClockwise size={32} /> Activity Log
          </h1>
          <p className="text-sm text-slate-600 mt-2">Every applied change — approved catalogue edits and all inventory activity.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCsv} className="gap-2 rounded-md" data-testid="export-log-csv-btn"><DownloadSimple size={16} /> Download CSV</Button>
          <Button variant="outline" onClick={load} className="gap-2 rounded-md" data-testid="refresh-log-btn"><ArrowClockwise size={16} /> Refresh</Button>
        </div>
      </header>

      <div className="flex flex-wrap items-end gap-3 mb-6">
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-44" data-testid="filter-action"><SelectValue placeholder="Action" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {Object.keys(ACTION_META).map((a) => <SelectItem key={a} value={a}>{ACTION_META[a].label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={entityFilter} onValueChange={setEntityFilter}>
          <SelectTrigger className="w-44" data-testid="filter-entity"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="manufacturer">Manufacturer</SelectItem>
            <SelectItem value="series">Series</SelectItem>
            <SelectItem value="product">Product</SelectItem>
            <SelectItem value="roll">Roll</SelectItem>
            <SelectItem value="purchase_order">Shipment / PO</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-mono tracking-[0.15em] uppercase text-slate-500">From</label>
          <Input type="date" value={fromDate} max={toDate || undefined} onChange={(e) => setFromDate(e.target.value)} className="w-40" data-testid="filter-from-date" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-mono tracking-[0.15em] uppercase text-slate-500">To</label>
          <Input type="date" value={toDate} min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)} className="w-40" data-testid="filter-to-date" />
        </div>
        {(fromDate || toDate) && (
          <Button variant="ghost" onClick={clearDates} className="gap-1.5 text-slate-500 rounded-md" data-testid="clear-dates-btn"><X size={14} /> Clear dates</Button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white p-16 text-center" data-testid="no-logs">
          <ClockCounterClockwise size={32} className="mx-auto text-slate-300 mb-3" />
          <div className="text-sm text-slate-500">{logs.length === 0 ? "No activity recorded yet." : "No activity in the selected date range."}</div>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([day, entries]) => (
            <div key={day}>
              <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-400 mb-3">{day}</div>
              <div className="border border-slate-200 bg-white divide-y divide-slate-100 rounded-md">
                {entries.map((l) => {
                  const meta = ACTION_META[l.action] || { label: l.action, cls: "bg-slate-100 text-slate-600 border-slate-200", Icon: PencilSimple };
                  const Icon = meta.Icon;
                  return (
                    <div key={l.id} className="flex items-start gap-4 px-4 py-3" data-testid={`log-${l.id}`}>
                      <div className={`mt-0.5 w-8 h-8 shrink-0 grid place-items-center rounded-full border ${meta.cls}`}>
                        <Icon size={15} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[9px] font-mono tracking-[0.15em] uppercase px-1.5 py-0.5 rounded-sm border ${meta.cls}`}>{meta.label}</span>
                          <span className="text-[9px] font-mono tracking-[0.15em] uppercase px-1.5 py-0.5 rounded-sm border bg-slate-50 text-slate-500 border-slate-200">{l.entity_type}</span>
                          <span className="text-sm text-slate-800">{l.description}</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
                          <span>{l.actor_name || l.actor_email}</span>
                          {l.actor_role && <span className={`text-[9px] font-mono uppercase tracking-wide px-1 py-0.5 rounded-sm border ${ROLE_CLS[l.actor_role] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{l.actor_role}</span>}
                          <span>·</span>
                          <span title={new Date(l.created_at).toLocaleString()}>{timeAgo(l.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
